// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link } from "react-router";

import Properties from "../../Properties";
import type {
  JsonOkResponseType,
  Json400ResponseType,
} from "../../global/types/JsonResponseType.js";
import type {
  ErrorsType,
  UserFormPropsType,
  UserFormStatesType,
} from "../types/UserFormType";
import SingleTextField from "../../global/components/SingleTextField";
import SingleSelect from "../../global/components/SingleSelect";
import {
  setErrorMsg,
  checkEmail,
  checkPassword,
  ErrosDisplay,
} from "../../global/components/ErrosDisplay";
import CompleteMessage from "../../global/components/CompleteMessage";
import { postInit } from "../../global/types/constants";
import Term from "../containers/Terms";
import { buttonStyles } from "../../global/components/StyledButtons";

import { ColorSchema, InvalidText } from "../../global/types/scss";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import * as Icons from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";

const DEBOUNCETIME = 500;

const PersonIcon = Icons["Person"];
const EmailIcon = Icons["Email"];
const PeopleIcon = Icons["People"];

class Signup extends Component<UserFormPropsType, UserFormStatesType> {
  timeout: number;
  constructor(props: UserFormPropsType) {
    super(props);
    this.signup = this.signup.bind(this);
    this.state = {
      conference: props.params.conference || "",
      errors: {},
      loading: false,
      email: "",
      firstname: "",
      organisation: "",
      lastname: "",
      termsagreed: false,
      completed: false,
      extraFields: {},
    };
  }

  CheckTerms = (): * => {
    this.setState({ termsagreed: true });
    this.setState({
      errors: setErrorMsg(this.state.errors, "termsagreed", "", true),
    });
  };

  handleChange = (e: Event & { currentTarget: HTMLInputElement }): * => {
    const errors = this.state.errors;
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    if (name === "termsagreed") {
      this.setState({ termsagreed: !this.state.termsagreed }, () => {
        if (!this.state.termsagreed) {
          this.setState({
            errors: setErrorMsg(
              errors,
              "termsagreed",
              "Please confirm you have read our Terms and Conditions."
            ),
          });
        } else {
          this.setState({
            errors: setErrorMsg(errors, "termsagreed", "", true),
          });
        }
      });
    } else {
      this.setState({
        [name]: value,
      });

      if (name === "email") {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(
          function (val: string) {
            this.setState({ errors: checkEmail(errors, val.trim()) });
          }.bind(this, value),
          DEBOUNCETIME
        );
      }
    }
  };

  handleExtraFieldChange = (
    field: string,
    type: string,
    is_required: boolean
  ) => (e: Event): * => {
    const value = e.target.value;
    const extraFields = { ...this.state.extraFields };
    extraFields[field] = value;
    this.setState(
      {
        extraFields: extraFields,
      },
      (): * => {
        if (type === "SELECTION" && is_required) {
          this.checkSelectRequired(field);
        }
      }
    );
  };

  checkSelectRequired = (field: string): * => {
    if (
      this.state.extraFields[field] === undefined ||
      this.state.extraFields[field] === ""
    ) {
      this.setState({
        errors: setErrorMsg(
          this.state.errors,
          "form",
          "Please Select value for " + field
        ),
      });
    } else {
      this.setState({
        errors: setErrorMsg(this.state.errors, "form", "", true),
      });
    }
  };

  checkAllRequiredSelection = (): * => {
    const extraFields = this.props.db.conference.additional_person_fields;
    extraFields.map((field: *): * => {
      if (field.type === "SELECTION" && field.is_required) {
        this.checkSelectRequired(field.name);
      }
    });
  };

  signup = async (e: Event): * => {
    const errors = this.state.errors;
    e.preventDefault();
    e.stopPropagation();

    if (!this.state.termsagreed) {
      this.setState({
        errors: setErrorMsg(
          errors,
          "termsagreed",
          "Please confirm you have read our Terms and Conditions."
        ),
      });
    }

    this.checkAllRequiredSelection();

    if (Object.keys(errors).length === 0) {
      this.setState((): * => ({ loading: true }));

      const postFormData = new FormData();
      const email = this.state.email.trim();
      const firstname = this.state.firstname.trim();
      const lastname = this.state.lastname.trim();
      const organisation = this.state.organisation.trim();

      postFormData.append("email", email);
      postFormData.append("first_name", firstname);
      postFormData.append("last_name", lastname);
      postFormData.append("organisation", organisation);

      const extraFields = this.state.extraFields;
      for (let key in extraFields) {
        postFormData.append(key, extraFields[key].trim());
      }

      const res: Response = await fetch(
        `${Properties.api_root}/api/v2/conferences/${this.props.db.conference.id}/people`,
        postInit(postFormData)
      );

      if (res.ok) {
        const jsonResponse: JsonOkResponseType = await res.json();
        this.setState({ completed: true });
      } else if (res.status === 400 || res.status === 409) {
        const jsonResponse: Json400ResponseType = await res.json();
        this.setState((state: UserFormStatesType): * => ({
          errors: jsonResponse.errors,
        }));
      }

      this.setState((): * => ({ loading: false }));
    }
  };

  render(): React$Element<*> {
    const classes = this.props.classes;
    const loading = this.state.loading;
    const errors = this.state.errors;
    let submitBtn;
    if (loading) {
      submitBtn = (
        <Button
          color="default"
          variant="raised"
          type="submit"
          id="submitBtn"
          className="disabled-btn"
          disabled
        >
          <CircularProgress
            size={14}
            color="inherit"
            style={{ marginRight: "5px" }}
          />
          Sending
        </Button>
      );
    } else {
      submitBtn = (
        <Button
          color="primary"
          variant="raised"
          type="submit"
          id="submitBtn"
          className={classes.primaryBtn}
          disabled={Object.keys(errors).length > 0}
        >
          Submit
        </Button>
      );
    }
    const extraFields = this.props.db.conference.additional_person_fields;
    const extraLines =
      extraFields &&
      extraFields.map((item: Object, i: number) => {
        let IconName = Icons["Info"];
        if (item.icon_name in Icons) {
          IconName = Icons[item.icon_name];
        }
        const field = item.name;
        if (item.type === "SELECTION") {
          return (
            <Grid item xs={12} key={i}>
              <Grid container spacing={8}>
                <Grid item xs={1} className="icon-block">
                  <IconName className="grey-icon medium-icon" />
                </Grid>
                <Grid item xs={11}>
                  <SingleSelect
                    isRequired={item.is_required}
                    label={field}
                    name={field}
                    value={this.state.extraFields[field] || ""}
                    onChange={this.handleExtraFieldChange(
                      field,
                      item.type,
                      item.is_required
                    )}
                    menus={item.values.map((list: string) => ({
                      label: list,
                      value: list,
                    }))}
                    isInvalid={errors.form !== undefined}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        } else if (item.type === "STRING") {
          return (
            <Grid item xs={12} key={i}>
              <Grid container spacing={8}>
                <Grid item xs={1} className="icon-block">
                  <IconName className="grey-icon medium-icon" />
                </Grid>
                <Grid item xs={11}>
                  <SingleTextField
                    isRequired={item.is_required}
                    label={field}
                    name={field}
                    value={this.state.extraFields[field] || ""}
                    onChange={this.handleExtraFieldChange(
                      field,
                      item.type,
                      item.is_required
                    )}
                    inpuType="text"
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        } else if (item.type === "DATE") {
          return (
            <Grid item xs={12} key={i}>
              <Grid container spacing={8}>
                <Grid item xs={1} className="icon-block">
                  <IconName className="grey-icon medium-icon" />
                </Grid>
                <Grid item xs={11}>
                  <SingleTextField
                    isRequired={item.is_required}
                    label={field}
                    name={field}
                    value={this.state.extraFields[field] || "dd/mm/yyyy"}
                    onChange={this.handleExtraFieldChange(
                      field,
                      item.type,
                      item.is_required
                    )}
                    inpuType="date"
                    isShrink
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        } else {
          return <div key={i}></div>;
        }
      });

    if (this.state.completed) {
      return (
        <Grid container spacing={8}>
          <Grid item xs={1}></Grid>
          <Grid item xs={10}>
            <CompleteMessage
              title="Sign Up Complete"
              content="Thank you<br>
              Your account has been created<br>
              and a Vertification Email has been sent.<br>
              Click on the link included in the Email to<br>
              Activate your account and Upload your photos."
              onClick={null}
            />
          </Grid>
        </Grid>
      );
    } else {
      return (
        <form onSubmit={(!loading && this.signup) || undefined}>
          <Typography
            variant="subheading"
            className="form-text hint-margin-bottom"
            style={{ color: ColorSchema("primary") }}
          >
            Please provide the below information.
          </Typography>
          <Grid container spacing={8}>
            <Grid item xs={1} className="icon-block">
              <PersonIcon className="grey-icon medium-icon" />
            </Grid>
            <Grid item xs={11} md={5}>
              <SingleTextField
                isRequired={true}
                label="First Name"
                name="firstname"
                value={this.state.firstname}
                onChange={this.handleChange}
                inpuType="text"
              />
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={11} md={5}>
              <SingleTextField
                isRequired={true}
                label="Last Name"
                name="lastname"
                value={this.state.lastname}
                onChange={this.handleChange}
                inpuType="text"
              />
            </Grid>
            <Grid item xs={1} className="icon-block">
              <EmailIcon className="grey-icon medium-icon" />
            </Grid>
            <Grid item xs={11}>
              <SingleTextField
                isRequired={true}
                label="Email Address"
                name="email"
                value={this.state.email}
                onChange={this.handleChange}
                inpuType="text"
                isInvalid={errors.email !== undefined}
              />
            </Grid>
            <Grid item xs={1} className="icon-block">
              <PeopleIcon className="grey-icon medium-icon" />
            </Grid>
            <Grid item xs={11}>
              <SingleTextField
                isRequired={true}
                label="Organisation / Company"
                name="organisation"
                value={this.state.organisation}
                onChange={this.handleChange}
                inpuType="text"
              />
            </Grid>
            {extraLines}
            <Grid item xs={2} md={1} style={{ marginTop: "5px" }}>
              <Checkbox
                value="true"
                name="termsagreed"
                checked={this.state.termsagreed}
                style={
                  errors.termsagreed !== undefined
                    ? { color: InvalidText }
                    : { color: ColorSchema("primary") }
                }
                onChange={this.handleChange}
              />
            </Grid>
            <Grid item xs={10} md={11} style={{ marginTop: "20px" }}>
              <Typography
                variant="body1"
                style={{ color: ColorSchema("primary") }}
              >
                <span className="form-hint">
                  {"I " +
                    (this.state.termsagreed ? "agreed" : "agree") +
                    " to the "}
                </span>
                <Term CheckTerms={this.CheckTerms} />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ErrosDisplay errors={errors} form="signup" />
            </Grid>
            <Grid item xs={12} style={{ marginBottom: "3%" }}>
              <div className="form-btn-block">{submitBtn}</div>
            </Grid>
            <Grid item xs={12} style={{ marginBottom: "2%" }}>
              <Divider light />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body1"
                className="form-text"
                style={{ color: ColorSchema("primary") }}
              >
                Already registered?{" "}
                <Link
                  to={"/" + this.state.conference + "/forgot"}
                  className="form-link-green"
                  style={{ color: ColorSchema("primary") }}
                >
                  Update your profile and photo here.
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>
      );
    }
  }
}

function mapStateToProps(state) {
  return { db: state.forensic.db };
}

export default connect(mapStateToProps, null)(withStyles(buttonStyles)(Signup));



// WEBPACK FOOTER //
// ./src/conference/views/Signup.js